import { Badge, Icon, Select } from '@forecastify/ui';
import type { RemoteUser, UserIdentity } from '@forecastify/shared';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The wizard's people step — item 67, and it is a reversal.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS STEP WAS DELIBERATELY REMOVED IN C34 AND THE 9 Sep 2026 ROUND ASKED FOR IT BACK.**
 * `contract.ts` still carries the original argument, and it was a good one: *"pairing a remote
 * account with a member is not a setting — it is an ongoing fact about people that changes when
 * somebody joins either system, months after any wizard was last opened."* Both things are true.
 * What the removal got wrong is that **connecting an instance is exactly when the pairing is worth
 * doing** — every remote name is unrecognised at that moment, and an unrecognised remote user is
 * an hour that cannot be stored at all.
 *
 * ⚠️⚠️ **SO IT IS BOTH, NOT A MOVE BACK.** People management keeps its pairing surface — that is
 * where an ongoing fact belongs, and where somebody joining next month is dealt with. This step is
 * the first pass, at the moment the list is longest and nobody has looked at it yet.
 *
 * ⚠️ **It edits the SAME rows through the same endpoint** (`PUT /integrations/identities/:id`), so
 * there is no second notion of a pairing and no reconciliation between two screens. The wizard's
 * draft does not carry these: a pairing is written when it is chosen, because it is a fact about
 * two systems rather than a setting on this form, and holding it in a draft would lose it if
 * somebody closed the wizard on the last step.
 *
 * ⚠️ **`oauth` pairings are shown without a control**, which is `0085`'s rule and the person rail's
 * behaviour: the account holder consented and Atlassian named them, so the server refuses a change
 * and offering a picker would be offering an action that fails.
 */
export function PeopleStep({
  t,
  identities,
  members,
  needsSave,
  preview,
  busy,
  onPair,
}: {
  readonly t: Translate;
  readonly identities: readonly UserIdentity[];
  readonly members: readonly { readonly id: string; readonly name: string }[];
  /** ⚠️ True while the instance has no id — an identity row points at one. */
  readonly needsSave: boolean;
  /**
   *    * ┌───────────────────────────────────────────────────────────────────────┐
   *    * │ Who is over there, before anything is saved — the connection test's answer.
   *    * └───────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **THE STEP USED TO BE BLANK UNTIL THE WIZARD HAD BEEN FINISHED AND REOPENED**, which is
   * the third report of one shape: *"when I connect, the people list doesn't load until I save the
   * instance, why not? can't we load this right away?"* The statuses and the custom fields were
   * moved onto the connection proof for the same complaint, and the accounts now ride along with
   * them.
   *
   * ⚠️⚠️ **A ROSTER, NOT PICKERS — AND THAT LIMIT IS REAL RATHER THAN LAZY.** A pairing is a row
   * keyed to an instance; before the instance exists there is nothing to pair to and nowhere to
   * store the answer. What the preview buys is the check somebody actually wants at this moment —
   * *is this the site I meant, are these my colleagues* — at the point where changing the
   * credential is still free.
   */
  readonly preview: readonly RemoteUser[];
  readonly busy: boolean;
  readonly onPair: (identityId: string, memberId: string | null) => void;
}) {
  if (needsSave) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-muted-foreground max-w-[70ch] text-[12px] leading-relaxed">
          {t('integrations.people.saveFirst')}
        </p>

        {preview.length === 0 ? null : (
          <>
            <p className="text-[12px]">
              {t('integrations.people.preview', { count: preview.length })}
            </p>
            <div className="bg-card overflow-hidden rounded-xl border shadow-sm">
              {preview.map((person) => (
                <div
                  key={person.remoteUserId}
                  className="flex items-center gap-2 border-b px-3 py-2 last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12.5px]">{person.name}</div>
                    {person.email === null ? null : (
                      <div className="text-muted-foreground truncate text-[11.5px]">
                        {person.email}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  if (identities.length === 0) {
    return (
      <p className="text-muted-foreground max-w-[70ch] text-[12px] leading-relaxed">
        {t('integrations.people.none')}
      </p>
    );
  }

  /**
   * ⚠️⚠️ **THE UNPAIRED FIRST, AND THAT ORDERING IS THE WHOLE VALUE OF THE STEP.** A site with two
   * hundred accounts and six unpaired ones sorted by name is a step nobody finishes — the six are
   * scattered through eight screens of rows that need nothing. This is the same argument
   * `fields-step.tsx` makes about eighty-five Jira fields, and the same answer.
   */
  const rows = [...identities].sort((left, right) => {
    if ((left.memberId === null) !== (right.memberId === null))
      return left.memberId === null ? -1 : 1;
    return left.remoteName.localeCompare(right.remoteName);
  });
  const unpaired = rows.filter((row) => row.memberId === null).length;

  return (
    <div className="flex flex-col gap-3" data-testid="integration-people">
      <p className="text-muted-foreground text-[12px] leading-relaxed">
        {t('integrations.people.lede')}
      </p>

      {unpaired > 0 && (
        <p className="border-destructive/40 bg-destructive/5 rounded-md border px-3 py-2 text-[12px]">
          {t('integrations.people.unpaired', { count: unpaired })}
        </p>
      )}

      <div className="bg-card overflow-hidden rounded-xl border shadow-sm">
        {rows.map((identity) => (
          <div
            key={identity.id}
            className="flex flex-wrap items-center gap-2 border-b px-3 py-2 last:border-0"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px]">{identity.remoteName}</div>
              {identity.remoteEmail === null ? null : (
                <div className="text-muted-foreground truncate text-[11.5px]">
                  {identity.remoteEmail}
                </div>
              )}
            </div>

            {identity.matchedBy === 'oauth' ? (
              /* ⚠️ Read-only, and it says why it is: `0085`. */
              <Badge variant="secondary" className="gap-1">
                <Icon name="shield-check" size={12} />
                {identity.memberName ?? ''}
              </Badge>
            ) : (
              <Select
                /* U-14 · a list of records, so it searches. */
                searchable
                searchPlaceholder={t('list.searchOptions')}
                className="h-8 w-[220px] max-w-full text-[12.5px]"
                aria-label={t('integrations.people.pairWith', { name: identity.remoteName })}
                disabled={busy}
                value={identity.memberId ?? ''}
                onChange={(event) => {
                  onPair(identity.id, event.target.value === '' ? null : event.target.value);
                }}
                options={[
                  { value: '', label: t('integrations.people.nobody') },
                  ...members.map((member) => ({ value: member.id, label: member.name })),
                ]}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
